import { forwardRef } from 'react'

import { cn } from '@/lib/utils'
import { FxIcon } from './FxIcon'
import type { FxStatus } from './FxStatusLabel'

/**
 * The cockpit's processing summary: a 240px ring split by document status
 * over the total, with a legend of counts and, per line, the action that
 * deals with that status ("Unklarheiten beheben", "Ergebnisse herunterladen").
 *
 * The ring is drawn as one `circle` per segment with a dash pattern rather
 * than as paths, which is what keeps the 8px gaps between segments even as
 * the counts change. It is hidden from the accessibility tree: a screen
 * reader gets the numbers instead, from the centre figure and the legend —
 * or from a plain sentence when the legend is turned off.
 *
 * Geometry (radius 106, stroke 28, gap 8 on a 240 box) is from the reference
 * implementation; none of it is a token, so it is written out here.
 */

type Counts = Partial<Record<FxStatus, number>>

const RADIUS = 106
const CIRCUMFERENCE = 2 * Math.PI * RADIUS
const GAP = 8

/** Drawing order around the ring, and each segment's stroke. */
const SEGMENTS: { key: FxStatus; stroke: string }[] = [
  { key: 'done', stroke: 'stroke-success' },
  { key: 'neu', stroke: 'stroke-fill' },
  { key: 'unclear', stroke: 'stroke-danger' },
  { key: 'inProgress', stroke: 'stroke-warning' },
]

/**
 * Legend order, glyph and wording. `neu` reads `text-muted` here, not the
 * fainter `text-disabled` of `FxStatusLabel`: in a legend the word is the
 * content, not a background state on a row someone is scanning past.
 */
const LEGEND: { key: FxStatus; icon: string; tone: string; one: string; many: string }[] = [
  { key: 'neu', icon: 'refresh', tone: 'text-text-muted', one: 'Formular noch nicht verarbeitet', many: 'Formulare noch nicht verarbeitet' },
  { key: 'inProgress', icon: 'schedule', tone: 'text-warning-text', one: 'Formular in Verarbeitung', many: 'Formulare in Verarbeitung' },
  { key: 'unclear', icon: 'error', tone: 'text-danger-text', one: 'Formular unklar', many: 'Formulare unklar' },
  { key: 'done', icon: 'check_circle', tone: 'text-success-text', one: 'Formular analysiert', many: 'Formulare analysiert' },
]

export interface FxStatusDonutProps extends React.HTMLAttributes<HTMLDivElement> {
  counts: Counts
  /** The action button for a status, shown at the end of its legend line. */
  actions?: Partial<Record<FxStatus, React.ReactNode>>
  /** Off leaves the ring alone; the counts then go to screen readers only. */
  legend?: boolean
  /**
   * Ring diameter in px (default 240). The cockpit's artboard draws a
   * compact 96px ring beside its status lines rather than the 240px hero the
   * DS sheet shows, so the geometry scales rather than being duplicated.
   */
  size?: number
}

export const FxStatusDonut = forwardRef<HTMLDivElement, FxStatusDonutProps>(
  ({ counts, actions, legend = true, size = 240, className, ...props }, ref) => {
    const total = SEGMENTS.reduce((sum, s) => sum + (counts[s.key] ?? 0), 0)
    // An empty ring would divide by zero; every segment is 0 long anyway.
    const divisor = total || 1

    let offset = 0
    const arcs = SEGMENTS.map((segment) => {
      const length = ((counts[segment.key] ?? 0) / divisor) * CIRCUMFERENCE
      const dash = Math.max(length - GAP, 0)
      const arc = (
        <circle
          key={segment.key}
          cx={120}
          cy={120}
          r={RADIUS}
          fill="none"
          strokeWidth={28}
          strokeDasharray={`${dash} ${CIRCUMFERENCE - dash}`}
          strokeDashoffset={-offset}
          transform="rotate(-90 120 120)"
          className={segment.stroke}
        />
      )
      offset += length
      return arc
    })

    return (
      <div ref={ref} className={cn('flex flex-wrap items-center gap-10', className)} {...props}>
        <div className="relative flex-none" style={{ height: size, width: size }}>
          <svg viewBox="0 0 240 240" width={size} height={size} className="block" aria-hidden="true">
            {arcs}
          </svg>
          {/* At the compact size the ring is 96px across and only the total
              fits — the artboard drops the words there rather than shrinking
              them to something unreadable. */}
          {size >= 180 ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-body">
              <span>Total</span>
              <strong className="text-display">{total}</strong>
              <span>{total === 1 ? 'Formular' : 'Formulare'}</span>
            </div>
          ) : (
            <strong className="absolute inset-0 flex items-center justify-center text-tile-title">
              {total}
            </strong>
          )}
        </div>

        {legend ? (
          <ul className="grid list-none gap-[22px] p-0">
            {LEGEND.map((line) => {
              const n = counts[line.key] ?? 0
              return (
                <li
                  key={line.key}
                  className="grid min-h-control grid-cols-[22px_auto_auto] items-center gap-3 text-body"
                >
                  <FxIcon name={line.icon} size={20} className={line.tone} />
                  <span>
                    <b className="font-semibold">{n}</b> {n === 1 ? line.one : line.many}
                  </span>
                  {actions?.[line.key] ?? null}
                </li>
              )
            })}
          </ul>
        ) : (
          <p className="sr-only">
            {LEGEND.map((line) => {
              const n = counts[line.key] ?? 0
              return `${n} ${n === 1 ? line.one : line.many}. `
            }).join('')}
          </p>
        )}
      </div>
    )
  },
)
FxStatusDonut.displayName = 'FxStatusDonut'
